import { pxToRem } from '@modernhealth/vital-components';
import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { WelcomeScreen } from 'src/modules/dependents/components/WelcomeScreen';
import { layout, mediaQuery } from '@modernhealth/vital-foundation';
import { useHistory } from 'react-router-dom';
import {
    JoinModernHealthForm,
    JoinModernHealthFormValues,
} from '../components/JoinModernHealthForm';
import { TherapyForm, TherapyFormValues } from '../components/TherapyForm';
import { calculateAge } from '../helpers/calculateAge';
import { RequestSent, RequestSentProps } from '../components/RequestSent';
import { DependentEligibleForm, EligibleFormValues } from '../components/DependentEligibleForm';
import { NotEligibleDependent } from '../components/NotEligibleDependent';
import { EligibleToTherapy } from '../components/EligibleToTherapy';
import { Loading } from '../components/Loading';
import { ContactSupportModal } from '../components/ContactSupportModal';
import { EligibleToJoin } from 'src/modules/dependents/components/EligibleToJoin';
import { useSelector } from 'react-redux';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { useInviteDependentMutation } from 'src/modules/dependents/documents/inviteDependentMutation.generated';
import { appClient } from 'src/config/apollo';
import { InviteDependentInput } from 'src/generated/webapp_gql';
import { captureException, withScope } from '@sentry/browser';
import { capture } from 'src/shared/helpers/sentryCapture';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useInviteDependentsAnalytics } from '../events';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

const joinFormInitialValues = {
    firstName: '',
    lastName: '',
    language: '',
    email: '',
    acceptTerms: false,
};

const requestTherapyFormInitialValues = {
    firstName: '',
    lastName: '',
    language: '',
    zipCode: '',
    specialties: undefined,
    anythingElse: '',
    acceptTerms: false,
};

const MIN_AGE_ELIGIBLE_TO_JOIN_MH = 18;

export type EligibilityStatus = 'notEligible' | 'therapy' | 'join';
export type InviteDependentStepNames =
    | 'welcome'
    | 'eligibleForm'
    | 'loading'
    | 'notEligible'
    | 'eligibleToTherapy'
    | 'eligibleToJoin'
    | 'joinForm'
    | 'therapyForm'
    | 'requestSent';

const existingAccountWithEmailRegex = /there is already an account with this email/;

const parseBackendErrorToNotificationMessage = (error: string) => {
    if (existingAccountWithEmailRegex.test(error)) {
        return { message: 'dependents:notification.email_used', shouldLogError: false };
    }

    return { message: config.messages.genericError, shouldLogError: true };
};

export const InviteDependentPage: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const user = useSelector(getUserInformation);
    const flags = useFlags();
    const [isSupportModalVisible, setSupportModalVisible] = useState(false);
    const [state, setState] = useState<InviteDependentStepNames>('welcome');
    const [eligibilityStatus, setEligibilityStatus] = useState<EligibilityStatus>('notEligible');
    const [dependentInfo, setDependentInfo] = useState<RequestSentProps['dependentInfo']>({
        email: '',
        fullName: '',
    });
    const [inviteDependentMutation, { loading }] = useInviteDependentMutation({
        client: appClient,
    });

    const eligibleFormInitialValues = {
        country: user.country ?? '',
        dateOfBirth: '',
        dependentRelation: '',
    };

    const [eligibleFormValues, setEligibleFormValues] =
        useState<EligibleFormValues>(eligibleFormInitialValues);

    const analytics = useInviteDependentsAnalytics(state, true, eligibilityStatus);

    const onEligibleBasicForm = (values: EligibleFormValues, buttonCopy: string) => {
        analytics.trackClick('Add dependent', {
            funnel_direction: 'down',
            button_copy: buttonCopy,
        });

        setEligibleFormValues(values);
        const age = calculateAge(values.dateOfBirth);

        if (age >= MIN_AGE_ELIGIBLE_TO_JOIN_MH) {
            setEligibilityStatus('join');
        } else if (age >= flags.minimumDependentAge) {
            setEligibilityStatus('therapy');
        } else {
            setEligibilityStatus('notEligible');
        }

        setState('loading');
    };

    // TODO: CAT-920 was created to address type mismatches. Please please fix
    const submitForm = async (values: Omit<InviteDependentInput, 'platform'>) => {
        try {
            const result = await inviteDependentMutation({
                variables: {
                    inviteDependent: {
                        platform: 'web',
                        ...values,
                    },
                },
            });

            const data = result.data?.inviteDependent;

            if (data && data.__typename === 'InviteDependentErrorResponse') {
                const { message, shouldLogError } = parseBackendErrorToNotificationMessage(
                    data.message,
                );
                toastNotificationsService.renderErrorToast(t(message));

                if (shouldLogError) {
                    withScope(scope => {
                        scope.setExtra('error message', data.message);

                        captureException(new Error('Error during invite dependent mutation'));
                    });
                }
            }

            if (data && data.__typename === 'InviteDependentSuccessResponse') {
                setDependentInfo({
                    email: values.email ?? '',
                    fullName: `${values.firstName} ${values.lastName}`,
                });
                setState('requestSent');
            }
        } catch (error) {
            capture(error);
            captureException(error);
            toastNotificationsService.renderErrorToast(t(config.messages.genericError));
        }
    };

    const onSubmitBasicForm = async (values: JoinModernHealthFormValues, buttonCopy: string) => {
        analytics.trackClick('Submit invite form', {
            funnel_direction: 'down',
            button_copy: buttonCopy,
        });

        //We won't be saving 'acceptTerms' in the backend and the mutation isn't expecting it.
        //Until this changes, we'll remove it before submitting.
        await submitForm({
            ...eligibleFormValues,
            ..._.omit(values, ['acceptTerms']),
        });
    };

    const onSubmitMinorForm = async (
        values: TherapyFormValues, //: Omit<TherapyFormValues, 'acceptTerms'>,
        buttonCopy: string,
    ) => {
        analytics.trackClick('Submit request therapy form', {
            funnel_direction: 'down',
            button_copy: buttonCopy,
        });

        //We won't be saving 'acceptTerms' in the backend and the mutation isn't expecting it.
        //Until this changes, we'll remove it before submitting.
        await submitForm({
            ...eligibleFormValues,
            ..._.omit(values, ['acceptTerms']),
            specialties: values.specialties ? values.specialties.map(option => option.value) : [],
        });
    };

    const onInviteAnother = (buttonCopy: string) => {
        analytics.trackClick('Add another dependent', {
            funnel_direction: 'lateral',
            button_copy: buttonCopy,
        });

        setEligibleFormValues(eligibleFormInitialValues);
        setState('eligibleForm');
    };

    const onReturnHome = (buttonCopy: string) => {
        analytics.trackClick('Back to dashboard', {
            funnel_direction: 'up',
            button_copy: buttonCopy,
        });

        history.push('/');
    };

    const redirectToEligibilityStatus = () => {
        if (eligibilityStatus === 'notEligible') {
            return setState('notEligible');
        }

        if (eligibilityStatus === 'therapy') {
            return setState('eligibleToTherapy');
        }

        if (eligibilityStatus === 'join') {
            return setState('eligibleToJoin');
        }
    };

    const handleContactUsClick = (buttonCopy: string) => {
        analytics.trackClick('Open "Contact us" modal', {
            button_copy: buttonCopy,
        });

        setSupportModalVisible(true);
    };

    const mapStateToComponents: Record<InviteDependentStepNames, () => JSX.Element> = {
        welcome: () => (
            <WelcomeScreen
                onContinue={actionName => {
                    analytics.trackClick(actionName, {
                        funnel_direction: 'down',
                    });

                    setState('eligibleForm');
                }}
                onContactUs={handleContactUsClick}
            />
        ),
        eligibleForm: () => (
            <DependentEligibleForm
                onCancel={() => {
                    analytics.trackClick('Back to invite dependents homepage', {
                        funnel_direction: 'up',
                    });
                    setEligibleFormValues(eligibleFormInitialValues);
                    setState('welcome');
                }}
                onSubmit={onEligibleBasicForm}
                initialValues={eligibleFormValues}
                onContactUs={handleContactUsClick}
            />
        ),
        loading: () => <Loading onNext={redirectToEligibilityStatus} />,
        notEligible: () => (
            <NotEligibleDependent
                onReturnHome={onReturnHome}
                onContactSupport={handleContactUsClick}
                onInviteAnother={onInviteAnother}
            />
        ),
        eligibleToTherapy: () => (
            <EligibleToTherapy
                onReturnHome={onReturnHome}
                onRequestTherapy={() => {
                    analytics.trackClick('Request therapy');
                    setState('therapyForm');
                }}
            />
        ),
        eligibleToJoin: () => (
            <EligibleToJoin
                onReturnHome={onReturnHome}
                onInviteDependent={() => {
                    analytics.trackClick('Invite dependent');
                    setState('joinForm');
                }}
            />
        ),
        joinForm: () => (
            <JoinModernHealthForm
                onCancel={buttonCopy => {
                    analytics.trackClick('Back to invite dependents homepage', {
                        funnel_direction: 'up',
                        button_copy: buttonCopy,
                    });
                    setState('welcome');
                }}
                onSubmit={onSubmitBasicForm}
                isLoading={loading}
                initialValues={joinFormInitialValues}
            />
        ),
        therapyForm: () => (
            <TherapyForm
                onCancel={buttonCopy => {
                    analytics.trackClick('Back to check eligibility form', {
                        funnel_direction: 'up',
                        button_copy: buttonCopy,
                    });
                    setState('eligibleForm');
                }}
                onSubmit={onSubmitMinorForm}
                isLoading={loading}
                initialValues={requestTherapyFormInitialValues}
            />
        ),
        requestSent: () => (
            <RequestSent
                onInviteAnother={onInviteAnother}
                onReturnHome={onReturnHome}
                eligibilityStatus={eligibilityStatus}
                dependentInfo={dependentInfo}
            />
        ),
    };

    return (
        <Wrapper isWelcomeScreen={state === 'welcome'}>
            <div>{mapStateToComponents[state]()}</div>
            <ContactSupportModal
                isVisible={isSupportModalVisible}
                onModalClose={() => setSupportModalVisible(false)}
                stepName={state}
            />
        </Wrapper>
    );
};

const Wrapper = styled.div<{ isWelcomeScreen: boolean }>(({ isWelcomeScreen }) => ({
    display: 'flex',
    flex: 1,
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: pxToRem(isWelcomeScreen ? 45 : 82),
    paddingBottom: pxToRem(100),

    [mediaQuery.maxWidth('xl')]: {
        flexDirection: 'column-reverse',
    },
}));
